import { TutorialDefinition } from '../TutorialEngine.js';
import { JumpsRenderOptions } from './JumpsTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type JumpsScene = {
    gameState: string;
    renderOptions: JumpsRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1C5,2D1,3B2  (Jérémy's reference grid)
// Quarters are 3×3: NW rows0-2/cols0-2, NE cols3-5, SW rows3-5, SE rows3-5/cols3-5.
//   digit 1 @ C5 (SW)   digit 2 @ D1 (NE)   digit 3 @ B2 (NW)
// Full solution: C5C4EF4E4D4ND1E1E2WB2C2S  (score 402: A=4, B=2)
//   The 1→2 link is diagonal SW→NE, so it routes through an empty landing at F4 (SE);
//   2→3 (NE→NW) and 3→1 (NW→SW) are single adjacent jumps.
//
// Ramp serialization is `<landing><body…><launcher><dir>` per ramp, concatenated or
// comma-separated (JumpsGame.serialize); `+cells` appends handleClick steps for animation.

const SOLVED = 'C5C4EF4E4D4ND1E1E2WB2C2S';

const scenes: JumpsScene[] = [
    // Scene 1: Standard intro — the solved goal state.
    {
        gameState: SOLVED,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Jumps, build ramps so a jumper\nleaps through the digits in order', cell: 'C0', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Anatomy — landing, ramp body, launcher. One ramp (digit 2's).
    {
        gameState: 'D1E1E2W',  // landing D1 (digit 2), body E1, launcher E2 jumping W
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Landing\n(the jumper arrives here)', cell: 'C0', anchor: 'center' },
            { type: 'text-label', text: 'Ramp', cell: 'F1', anchor: 'center' },
            { type: 'text-label', text: 'Launcher\n(the jumper leaps off here)', cell: 'D3', anchor: 'center' },
        ],
    },

    // Scene 3: A launch lands in an adjacent quarter, same spot within it; a launcher
    // may not fire back along its own ramp.
    {
        gameState: 'D1E1E2W',  // NE→NW jump, landing on digit 3 at B2
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'A jump crosses into a neighbouring\nquarter, never diagonally', cell: 'C0', anchor: 'center' },
            { type: 'text-label', text: 'and lands at the same spot\nwithin that quarter', cell: 'C3', anchor: 'center' },
            { type: 'text-label', text: 'A launcher can never fire\nback along its own ramp', cell: 'D4', anchor: 'center', color: COLORS.amber },
        ],
    },

    // Scene 4: Every digit hosts a landing; not every landing is on a digit.
    {
        gameState: 'C5C4E,F4E4D4N',  // digit landing at C5, routing landing at empty F4
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Every digit must\nhold a landing', cell: 'B4', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Landings can also sit on empty cells,\nramps that just re-route the jumper', cell: 'D0', anchor: 'center' },
        ],
    },

    // Scene 5: Building — click a cell for the landing, extend, click the end to launch.
    {
        gameState: '',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a cell to\nplace a landing', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Click in line\nto build the ramp', cell: 'C3', anchor: 'center' },
            { type: 'text-label', text: 'Click the end\nto place the launcher', cell: 'E2', anchor: 'center' },
            { type: 'pulse', cell: 'B2', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                '',
                '+B2',           // landing on digit 3
                '+B2,C2',        // extend within NW
                '+B2,C2,C2',     // click the end again → launcher placed
            ],
        },
    },

    // Scene 6: Click the launcher to re-aim it; click the landing to delete the ramp.
    {
        gameState: 'B2C2S,D1E1E2W',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a launcher\nto change its direction', cell: 'B3', anchor: 'center' },
            { type: 'text-label', text: 'Click a landing\nto delete the ramp', cell: 'D0', anchor: 'center', color: COLORS.amber },
            { type: 'pulse', cell: 'C2', color: COLORS.teal },
            { type: 'pulse', cell: 'D1', color: COLORS.amber },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'B2C2S,D1E1E2W',
                'B2C2S,D1E1E2W+C2',       // re-aim the NW launcher
                'B2C2S,D1E1E2W+C2,D1',    // delete the NE ramp (click its landing)
            ],
        },
    },
];

/**
 * Jumps tutorial: 6 scenes on a 6×6 grid with digits 1-3.
 * See docs/tutorials/jumps.md for detailed scene descriptions.
 */
export const JUMPS_TUTORIAL: TutorialDefinition = {
    gameType: 'jumps',
    gridCode: '6/1-3:1C5,2D1,3B2',
    scenes,
};
